import { styled } from '@mui/styles'
import moment from 'moment'
import * as R from 'ramda'
import { DateUtils, type Nil } from '@pbt/pbt-ui-components'

import type { QuestionTimelineItemEntry } from '~/types'

export const getAnswers = (answers: QuestionTimelineItemEntry[]) => {
  const groupedAnswersByAppointment = answers.reduce(
    (updatedAnswers: Record<string, QuestionTimelineItemEntry[]>, current) => {
      const { appointmentId } = current
      return {
        ...updatedAnswers,
        [appointmentId]: [...(updatedAnswers[appointmentId] || []), current],
      }
    },
    {},
  )

  return Object.values(groupedAnswersByAppointment)
}

export const getAnswerDate = (entry: QuestionTimelineItemEntry[]) => {
  const initialDate = entry[0].date
  return entry.reduce((newestDate, current) => {
    if (moment(current.date).isAfter(newestDate)) {
      return current.date
    }
    return newestDate
  }, initialDate)
}

export const getText = (
  answerText: string | Nil,
  answerInputTexts: {
    inputTypeId: string
    value: string | Nil
  }[],
  t: (args: string, variable: { date: string }) => string | undefined,
  DateSelector: string,
) => {
  const filteredAnswerInputTexts = answerInputTexts.filter(
    ({ value }) => !R.isNil(value) && value !== '',
  )

  const answerInputTextString = filteredAnswerInputTexts.reduce(
    (acc, obj, index) => {
      const newLine = index !== 0 ? ' | ' : ''
      const isDateSelector = obj.inputTypeId === DateSelector
      if (isDateSelector) {
        const newValue =
          filteredAnswerInputTexts.length === 1
            ? // when the answer is a single date, add `Date provided` in front of the answer
              t('Clients:TIMELINE.QUESTION_CARD.DATE_PROVIDED', {
                date: DateUtils.formatDate(obj.value),
              })
            : DateUtils.formatDate(obj.value)
        return `${acc}${newLine}${newValue}`
      }
      return `${acc}${newLine}${obj.value}`
    },
    '',
  )

  if (answerText && !R.isEmpty(answerInputTextString)) {
    return `${answerText} | ${answerInputTextString}`
  }

  return `${answerText || ''}${answerInputTextString}`
}

export const StyledUnBulletedList = styled('ul')(() => ({
  listStyleType: 'none',
  padding: 0,
  margin: 0,
}))

export const getListComponent = (entries: QuestionTimelineItemEntry[]) => {
  const StyledBulletedList = styled('ul')(({ theme }) => ({
    listStyleType: 'disc',
    paddingLeft: theme.spacing(3),
    margin: 0,
    '& li': {
      paddingLeft: 0,
      marginLeft: 0,
    },
    '& p': {
      display: 'inline',
    },
  }))

  const hasMultipleSelected = entries.length > 1
  return hasMultipleSelected ? StyledBulletedList : StyledUnBulletedList
}
